import React, { useState, useCallback, useEffect } from 'react';
import { debounce } from 'lodash';
import { Search, Typography } from '@sanlam-glacier/app-common/components';
import styles from './case-tracking-advanced-search.module.scss';
import { InstructionCategory } from 'modules/case-tracking/types/filtering/filtering.enums';
import { CaseType } from 'modules/case-tracking/types/case-tracking.types';
import {
  blockIllegalCharacters,
  sanitizeIllegalCharacters
} from 'utils/string-handling/block-illegal-characters';

interface AdvancedSearchProps {
  data: CaseType[];
  onSearchResults: (results: CaseType[], searchTerm: string) => void;
  appliedFilters?: InstructionCategory;
  placeholder?: string;
  className?: string;
}

export const AdvancedSearch: React.FC<AdvancedSearchProps> = ({
  data,
  onSearchResults,
  appliedFilters,
  placeholder = 'Search cases by client name, ID, policy number...',
  className
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const normalizeText = (text: string): string => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  };

  const containsForbiddenCharacters = (text: string): boolean => {
    const forbiddenChars = /[<>/\\*;]/g;
    return forbiddenChars.test(text);
  };

  const extractSearchableFields = (caseItem: CaseType): string[] => {
    const clientNameParts = caseItem.clientName.split(' ');
    const firstName = clientNameParts[0] || '';
    const surname = clientNameParts.slice(1).join(' ') || '';

    return [
      firstName,
      surname,
      caseItem.clientId?.toString() ?? '',
      caseItem.instructionCategory ?? '',
      caseItem.investmentNumber?.toString() ?? '',
      caseItem.proposalNumber?.toString() ?? '',
      caseItem.instructionNumber?.toString() ?? '',
      caseItem.instructionType ?? '',
      caseItem.clientName ?? ''
    ].filter((field): field is string => Boolean(field));
  };

  const performSearch = useCallback((term: string, dataToSearch: CaseType[]): CaseType[] => {
    if (term.length < 3) {
      return dataToSearch;
    }

    if (containsForbiddenCharacters(term)) {
      return [];
    }

    const normalizedSearchTerm = normalizeText(term);
    const searchWords = normalizedSearchTerm.split(/\s+/).filter((word) => word.length > 0);

    const exactMatches: CaseType[] = [];
    const partialMatches: CaseType[] = [];

    dataToSearch.forEach((caseItem) => {
      const searchableFields = extractSearchableFields(caseItem);
      let hasExactMatch = false;
      let hasPartialMatch = false;

      searchableFields.forEach((field) => {
        const normalizedField = normalizeText(field);

        if (normalizedField.includes(normalizedSearchTerm)) {
          hasExactMatch = true;
        }

        const wordsFound = searchWords.every((word) =>
          searchableFields.some((f) => normalizeText(f).includes(word))
        );

        if (wordsFound) {
          hasPartialMatch = true;
        }
      });

      // Prioritize exact matches
      if (hasExactMatch && !exactMatches.some((item) => item.id === caseItem.id)) {
        exactMatches.push(caseItem);
      } else if (hasPartialMatch && !partialMatches.some((item) => item.id === caseItem.id)) {
        partialMatches.push(caseItem);
      }
    });

    // Return exact matches first, then partial matches
    return [
      ...exactMatches,
      ...partialMatches.filter((item) => !exactMatches.some((exact) => exact.id === item.id))
    ];
  }, []);

  const applyFilters = useCallback(
    (searchResults: CaseType[]): CaseType[] => {
      if (!appliedFilters || Object.keys(appliedFilters).length === 0) {
        return searchResults;
      }

      return searchResults.filter((item) => {
        return true;
      });
    },
    [appliedFilters]
  );

  const debouncedSearch = useCallback(
    debounce((term: string) => {
      setIsLoading(true);

      //Remove setTimeout once API is ready and integrated
      // Simulate API call delay
      setTimeout(() => {
        let results = performSearch(term, data);
        results = applyFilters(results);

        onSearchResults(results, term);
        setIsLoading(false);
      }, 1000);
    }, 300),
    [data, appliedFilters, performSearch, applyFilters, onSearchResults]
  );

  const handleSearchChange = (value: string) => {
    const cleanValue = sanitizeIllegalCharacters(value);

    debouncedSearch.cancel();

    if (!cleanValue) {
      setIsLoading(false);
      const filteredData = applyFilters(data);
      onSearchResults(filteredData, '');
      setSearchTerm('');
      return;
    }

    setSearchTerm(cleanValue);

    if (cleanValue.length < 3) {
      setIsLoading(false);
      const filteredData = applyFilters(data);
      onSearchResults(filteredData, cleanValue);
    } else {
      debouncedSearch(cleanValue);
    }
  };

  useEffect(() => {
    if (searchTerm.length >= 3) {
      debouncedSearch(searchTerm);
    } else {
      const filteredData = applyFilters(data);
      onSearchResults(filteredData, searchTerm);
    }
  }, [appliedFilters, data, debouncedSearch, searchTerm, applyFilters, onSearchResults]);

  const handleKeyPress = (event: KeyboardEvent) => {
    if (blockIllegalCharacters(event.key) || containsForbiddenCharacters(event.key)) {
      event.preventDefault();
    }
  };

  return (
    <div className={`${styles.advancedSearchContainer} ${className || ''}`}>
      <div className={styles.searchWrapper}>
        <Search
          data-testid={'search'}
          value={searchTerm}
          inputProps={{ onKeyPress: handleKeyPress }}
          onChange={handleSearchChange}
          placeholder={placeholder}
          className={styles.searchInput}
          loading={isLoading}
        />
        <Typography
          color={Typography.COLOR.INHERIT}
          variant={Typography.VARIANT.B4}
          weight={Typography.WEIGHT.NORMAL}
          className={styles.searchLabel}
          data-testid={'searchText'}
        >
          Search by instruction number, name, surname, ID, passport or registration number,
          instruction type or investment number
        </Typography>
      </div>
    </div>
  );
};
